// v6-polish — elapsed-time helper for the loading-state UI.
//
// The EyePreview overlay and the ChatPanel placeholder both show "how
// long has this generation been running?" once a request is in flight.
// Each of them previously sat on top of a static spinner + pulse dots,
// which is a poor signal for the user (mum in particular — a 30-second
// silent spinner is anxiety-inducing).
//
// `useElapsed(active, intervalMs)` returns the whole seconds since
// `active` flipped to true. While `active` is false it returns 0 and
// stops the interval, so the hook is cheap to keep mounted.
//
// Defaults to a 1-second tick — that's plenty for "Generating… 14s" and
// keeps the re-render count tiny. Pass a smaller interval if you need
// sub-second resolution.

import { useEffect, useRef, useState } from 'react';

export function useElapsed(active: boolean, intervalMs: number = 1000): number {
  const [elapsedSec, setElapsedSec] = useState(0);
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (active) {
      startedAtRef.current = Date.now();
      setElapsedSec(0);
    } else {
      startedAtRef.current = null;
      setElapsedSec(0);
    }
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => {
      const start = startedAtRef.current;
      if (start === null) return;
      setElapsedSec(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [active, intervalMs]);

  return elapsedSec;
}